<script setup>
/* eslint-disable no-console */
import { ref, computed, watch } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useAlert } from 'dashboard/composables';
import { resolveOT, updateCaseToClosed } from 'dashboard/api/cea';
import ServiceOrdersAPI from 'dashboard/api/serviceOrders';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  order: {
    type: Object,
    required: true,
  },
  aquasisData: {
    type: Object,
    default: null,
  },
  fieldVisibility: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close', 'resolved']);

const dialogRef = ref(null);
const isSubmitting = ref(false);

// Form state
const endDateOt = ref('');
const endLastTaskOt = ref('');
const finalSolution = ref('');
const solutionDescription = ref('');
const nonExecutionMotive = ref('');
const executorIdentifier = ref('');
const executorName = ref('');
const transmitterInstalled = ref(false);
const suspensionLevel = ref(0);
const resolutionElements = ref([]);

const nowDatetime = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const ensureArray = val => {
  if (Array.isArray(val)) return val;
  if (val !== null && val !== undefined) return [val];
  return [];
};

const requiresMeter = computed(() => {
  return props.fieldVisibility.meter !== false;
});

const buildResolutionElements = () => {
  if (!requiresMeter.value) return [];
  const elements = ensureArray(props.aquasisData?.otElements);
  if (elements.length > 0) {
    return elements.map(el => ({
      serialNumber: el.serialNumber || '',
      meterBrandID: el.meterBrandID || '',
      meterModel: el.meterModel || '',
      meterGauge: el.meterGauge || '',
      meterDial: el.meterDial || 0,
      communicationModule: el.communicationModule || '',
      manufacturedYear: el.manufacturedYear || 0,
      installationDate: el.installationDate || '',
      emplacement: el.emplacement || '',
      key: el.key || 0,
      batteryRow: el.batteryRow || 0,
      batteryColumn: el.batteryColumn || 0,
      valveRetention:
        el.valveRetention === 'true' || el.valveRetention === true,
      installedOrRetired: false,
      dateReading: nowDatetime(),
      readingRegister: '',
    }));
  }
  return [
    {
      serialNumber: '',
      meterBrandID: '',
      meterModel: '',
      meterGauge: '',
      meterDial: 0,
      communicationModule: '',
      manufacturedYear: 0,
      installationDate: '',
      emplacement: '',
      key: 0,
      batteryRow: 0,
      batteryColumn: 0,
      valveRetention: false,
      installedOrRetired: false,
      dateReading: nowDatetime(),
      readingRegister: '',
    },
  ];
};

const resetForm = () => {
  endDateOt.value = nowDatetime();
  endLastTaskOt.value = nowDatetime();
  finalSolution.value = '';
  solutionDescription.value = '';
  nonExecutionMotive.value = '';
  executorIdentifier.value = '';
  executorName.value = '';
  transmitterInstalled.value = false;
  suspensionLevel.value = 0;
  resolutionElements.value = buildResolutionElements();
};

const isFormValid = computed(() => {
  const hasBasicFields =
    endDateOt.value && endLastTaskOt.value && finalSolution.value.trim();
  if (!requiresMeter.value) return hasBasicFields;
  const hasReading = resolutionElements.value.some(
    el => el.readingRegister !== '' && el.readingRegister !== null
  );
  return hasBasicFields && hasReading;
});

const handleClose = () => {
  resetForm();
  emit('close');
};

const handleSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) return;

  try {
    isSubmitting.value = true;

    const payload = {
      operationalSiteID: props.aquasisData?.otData?.operationalSiteID || '',
      installationID: props.aquasisData?.otData?.installationID || '',
      systemOrigin: '',
      otClass: '0',
      otOrigin: props.order.aquasis_order_id,
      endDateOt: new Date(endDateOt.value).toISOString(),
      endLastTaskOt: new Date(endLastTaskOt.value).toISOString(),
      finalSolution: finalSolution.value,
      solutionDescription: solutionDescription.value,
      nonExecutionMotive: nonExecutionMotive.value,
      executorIdentifier: executorIdentifier.value,
      executorName: executorName.value,
      companyExecutorIdentifier: '',
      companyExecutorName: '',
      transmitterInstalled: transmitterInstalled.value ? '1' : '0',
      language: 'es',
      suspensionLevel: String(suspensionLevel.value),
      longitude: '',
      latitude: '',
      coordinatesType: '',
      codificationType: '',
      captureDate: '',
      resolutionElements: requiresMeter.value
        ? resolutionElements.value.map(el => ({
            ...el,
            dateReading: el.dateReading
              ? new Date(el.dateReading).toISOString()
              : '',
          }))
        : [],
    };

    const xmlDoc = await resolveOT(payload);

    const returnEl =
      xmlDoc.getElementsByTagName('return')[0] ||
      xmlDoc.getElementsByTagName('resolveOTReturn')[0] ||
      xmlDoc.getElementsByTagName('resolveOTResponse')[0];

    const resultText = returnEl?.textContent?.trim()?.toLowerCase();
    const success = resultText === 'true' || resultText === '1';

    if (!success) {
      useAlert('Aquasis no pudo resolver la orden. Verifique los datos.');
      return;
    }

    // Update local status to field_complete
    await ServiceOrdersAPI.update(props.order.id, { status: 'field_complete' });

    // Close CEA case (best-effort, non-blocking)
    const caseId =
      props.order.ticket?.custom_attributes?.caso_sn ||
      props.order.ticket?.custom_attributes?.sys_id ||
      props.order.ticket?.metadata?.caso_sn ||
      props.order.ticket?.metadata?.sys_id;
    if (caseId) {
      updateCaseToClosed(caseId, '', '').catch(err =>
        console.error('Failed to close CEA case:', err)
      );
    }

    useAlert('Orden resuelta exitosamente');
    emit('resolved');
    handleClose();
  } catch (error) {
    console.error('Error resolving order:', error);
    useAlert('Error al resolver la orden');
  } finally {
    isSubmitting.value = false;
  }
};

watch(
  () => props.show,
  newVal => {
    if (newVal) {
      resetForm();
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <Dialog
    ref="dialogRef"
    width="lg"
    title="Resolver Orden"
    overflow-y-auto
    :is-loading="isSubmitting"
    :confirm-button-label="isSubmitting ? 'Resolviendo...' : 'Resolver Orden'"
    :disable-confirm-button="!isFormValid || isSubmitting"
    @close="handleClose"
    @confirm="handleSubmit"
  >
    <div class="flex flex-col gap-4">
      <!-- Order reference -->
      <div class="bg-n-solid-3 rounded-lg p-3 border border-n-weak">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-11">Orden Aquasis</span>
          <span class="font-mono text-sm font-semibold text-n-brand">
            {{ order.aquasis_order_id }}
          </span>
        </div>
      </div>

      <!-- Dates -->
      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            Fecha de Cierre <span class="text-n-ruby-11">*</span>
          </label>
          <input
            v-model="endDateOt"
            type="datetime-local"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            Fin Ultima Tarea <span class="text-n-ruby-11">*</span>
          </label>
          <input
            v-model="endLastTaskOt"
            type="datetime-local"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
      </div>

      <!-- Solution -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          Solucion Final <span class="text-n-ruby-11">*</span>
        </label>
        <input
          v-model="finalSolution"
          type="text"
          placeholder="Codigo o descripcion de la solucion final"
          class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          Descripcion Solucion
        </label>
        <textarea
          v-model="solutionDescription"
          rows="3"
          placeholder="Descripcion detallada de la solucion..."
          class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand resize-none"
        />
      </div>

      <!-- Non-execution motive -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          Motivo No Ejecucion
        </label>
        <input
          v-model="nonExecutionMotive"
          type="text"
          placeholder="Si aplica, motivo de no ejecucion"
          class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
        />
      </div>

      <!-- Executor -->
      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            ID Ejecutor
          </label>
          <input
            v-model="executorIdentifier"
            type="text"
            placeholder="Identificador del ejecutor"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            Nombre Ejecutor
          </label>
          <input
            v-model="executorName"
            type="text"
            placeholder="Nombre del ejecutor"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
      </div>

      <!-- Resolution Elements (Meter) — only for order types that require meter -->
      <template v-if="requiresMeter">
        <div class="flex flex-col gap-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            Elementos / Medidor
          </h3>
          <div
            v-for="(el, idx) in resolutionElements"
            :key="idx"
            class="rounded-lg border border-n-weak p-3 flex flex-col gap-3"
          >
            <div
              v-if="resolutionElements.length > 1"
              class="text-xs font-medium text-n-slate-11"
            >
              Elemento {{ idx + 1 }}
            </div>
            <!-- Pre-populated meter info (read-only) -->
            <div class="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div class="flex flex-col">
                <span class="text-xs text-n-slate-10">No. Serie</span>
                <span class="text-sm text-n-slate-12">
                  {{ el.serialNumber || '-' }}
                </span>
              </div>
              <div class="flex flex-col">
                <span class="text-xs text-n-slate-10">Marca</span>
                <span class="text-sm text-n-slate-12">
                  {{ el.meterBrandID || '-' }}
                </span>
              </div>
              <div class="flex flex-col">
                <span class="text-xs text-n-slate-10">Modelo</span>
                <span class="text-sm text-n-slate-12">
                  {{ el.meterModel || '-' }}
                </span>
              </div>
              <div class="flex flex-col">
                <span class="text-xs text-n-slate-10">Calibre</span>
                <span class="text-sm text-n-slate-12">
                  {{ el.meterGauge || '-' }}
                </span>
              </div>
            </div>
            <!-- Editable fields -->
            <div class="grid grid-cols-2 gap-3">
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-n-slate-12">
                  Lectura Actual <span class="text-n-ruby-11">*</span>
                </label>
                <input
                  v-model="el.readingRegister"
                  type="number"
                  step="any"
                  placeholder="Lectura del medidor"
                  class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-n-slate-12">
                  Fecha Lectura
                </label>
                <input
                  v-model="el.dateReading"
                  type="datetime-local"
                  class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
                />
              </div>
            </div>
            <div
              class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak w-fit"
            >
              <input
                :id="`installedOrRetired-${idx}`"
                v-model="el.installedOrRetired"
                type="checkbox"
                class="accent-woot-500"
              />
              <label
                :for="`installedOrRetired-${idx}`"
                class="text-sm font-medium text-n-slate-12 cursor-pointer"
              >
                Retirado
              </label>
            </div>
          </div>
        </div>

        <!-- Transmitter & Suspension -->
        <div class="grid grid-cols-2 gap-3">
          <div
            class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak"
          >
            <input
              id="transmitterInstalled"
              v-model="transmitterInstalled"
              type="checkbox"
              class="accent-woot-500"
            />
            <label
              for="transmitterInstalled"
              class="text-sm font-medium text-n-slate-12 cursor-pointer"
            >
              Transmisor Instalado
            </label>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              Nivel Suspension
            </label>
            <input
              v-model.number="suspensionLevel"
              type="number"
              min="0"
              class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
            />
          </div>
        </div>
      </template>

      <div class="text-xs text-n-slate-10">
        <span class="text-n-ruby-11">*</span> Campos requeridos
      </div>
    </div>
  </Dialog>
</template>
